import 'vanilla-cookieconsent/dist/cookieconsent';
import Ga from '../Ga';

export default class Cookie {
	constructor() {
		this.element = document.body;
		this.language = document.getElementById('appCountry').value;
		this.init();
	}

	init() {
		const cc = initCookieConsent();

		// Expose the consent instance so other components (ComponentLoader) can check consent
		// before firing any analytics/tracking on a normal page load / after AJAX.
		window.cookieConsent = cc;

		cc.run({
			current_lang: this.language,
			autoclear_cookies: true,
			page_scripts: true,

			// Start custom analytics (dataLayer page_view + event handlers) the moment the visitor
			// grants the 'analytics' category — without needing a reload. The GA/GTM loader <script>s
			// themselves are gated separately via type="text/plain" data-cookiecategory="analytics"
			// (handled by page_scripts). onFirstAction/onChange do NOT fire on a plain page load, so
			// steady-state firing stays solely in ComponentLoader and there is no double page_view.
			onFirstAction: () => {
				if (cc.allowedCategory('analytics')) {
					(new Ga()).onPageView();
				}
			},

			onChange: (cookie, changedCategories) => {
				if (changedCategories.indexOf('analytics') === -1) {
					return;
				}

				if (cc.allowedCategory('analytics')) {
					(new Ga()).onPageView();
				} else {
					// Analytics consent revoked → reload so already-loaded GA/GTM stops and
					// autoclear_cookies removes the analytics cookies.
					window.location.reload();
				}
			},

			gui_options: {
				consent_modal: {
					layout: 'cloud',
					transition: 'zoom'
				},
				settings_modal: {
					transition: 'zoom'
				}
			},

			languages: {
				// Keyed by the appCountry value (cz/sk/pl) so current_lang matches exactly instead of
				// relying on cookieconsent's fallback-to-first-language behaviour.
				'cz': {
					consent_modal: {
						title: 'Nastavení cookies 🍪',
						description: 'Tyto stránky používají soubory Cookies. <button type="button" data-cc="c-settings" class="cc-link">Více informací</button>',
						primary_btn: {
							text: 'Přijmout',
							role: 'accept_all'
						},
						secondary_btn: {
							text: 'Zamítnout',
							role: 'accept_necessary'
						}
					},
					settings_modal: {
						title: 'Nastavení cookies',
						save_settings_btn: 'Uložit nastavení',
						accept_all_btn: 'Přijmout vše',
						reject_all_btn: 'Zamítnout vše',
						close_btn_label: 'Zavřít',
						cookie_table_headers: [
							{col1: 'Název'},
							{col2: 'Doména'},
							{col3: 'Expirace'}
						],
						blocks: [
							{
								title: 'Používání cookies 📢',
								description: 'Soubory cookie používáme k zajištění základních funkcí webu a ke zlepšení vašeho online zážitku. Pro každou kategorii si můžete vybrat, zda se chcete přihlásit/odhlásit, kdykoli budete chtít. Další podrobnosti týkající se souborů cookie a dalších citlivých údajů naleznete v úplném znění <a href="https://www.se.com/cz/cs/about-us/legal/data-privacy" class="cc-link" target="_blank" rel="noopener noreferrer">zásady ochrany osobních údajů</a>.'
							}, {
								title: 'Technicky nezbytné cookies',
								description: 'Tyto soubory cookie jsou nezbytné pro správné fungování webových stránek. Bez těchto cookies by web nefungoval správně.',
								toggle: {
									value: 'necessary',
									enabled: true,
									readonly: true
								}
							}, {
								title: 'Analytické cookies',
								description: 'Tyto soubory cookie nám pomáhají pochopit, jak návštěvníci web používají. Sbírají se anonymně a spouští se pouze s vaším souhlasem.',
								toggle: {
									value: 'analytics',
									enabled: false,
									readonly: false
								},
								cookie_table: [
									{
										col1: '_ga',
										col2: 'google.com',
										col3: '2 roky',
										is_regex: true
									},
									{
										col1: '_gid',
										col2: 'google.com',
										col3: '24 hodin'
									},
									{
										col1: '_gat',
										col2: 'google.com',
										col3: '1 minuta',
										is_regex: true
									}
								]
							},
							{
								title: 'Více informací',
								description: 'Máte-li jakékoli dotazy týkající se našich zásad týkajících se souborů cookie a vašich voleb, prosím <a class="cc-link" href="https://www.se.com/cz/cs/work/support" target="_blank" rel="noopener noreferrer">kontaktujte nás</a>.'
							}
						]
					}
				},
				'sk': {
					consent_modal: {
						title: 'Nastavenie cookies 🍪',
						description: 'Tieto stránky používajú súbory Cookies. <button type="button" data-cc="c-settings" class="cc-link">Viac informácií</button>',
						primary_btn: {
							text: 'Prijať',
							role: 'accept_all'
						},
						secondary_btn: {
							text: 'Odmietnuť',
							role: 'accept_necessary'
						}
					},
					settings_modal: {
						title: 'Nastavenie cookies',
						save_settings_btn: 'Uložiť nastavenia',
						accept_all_btn: 'Prijať všetko',
						reject_all_btn: 'Odmietnuť všetko',
						close_btn_label: 'Zavrieť',
						cookie_table_headers: [
							{col1: 'Názov'},
							{col2: 'Doména'},
							{col3: 'Expirácia'}
						],
						blocks: [
							{
								title: 'Používanie cookies 📢',
								description: 'Súbory cookie používame na zabezpečenie základných funkcií webu a na zlepšenie vášho online zážitku. Pre každú kategóriu si môžete vybrať, či sa chcete prihlásiť/odhlásiť, kedykoľvek budete chcieť. Ďalšie podrobnosti týkajúce sa súborov cookie a ďalších citlivých údajov nájdete v úplnom znení <a href="https://www.schneider-electric.sk/sk/about-us/legal/data-privacy" class="cc-link" target="_blank" rel="noopener noreferrer">zásad ochrany osobných údajov</a>.'
							}, {
								title: 'Technicky nevyhnutné cookies',
								description: 'Tieto súbory cookie sú nevyhnutné pre správne fungovanie webových stránok. Bez týchto cookies by web nefungoval správne.',
								toggle: {
									value: 'necessary',
									enabled: true,
									readonly: true
								}
							}, {
								title: 'Analytické cookies',
								description: 'Tieto súbory cookie nám pomáhajú pochopiť, ako návštevníci web používajú. Zbierajú sa anonymne a spúšťajú sa iba s vaším súhlasom.',
								toggle: {
									value: 'analytics',
									enabled: false,
									readonly: false
								},
								cookie_table: [
									{
										col1: '_ga',
										col2: 'google.com',
										col3: '2 roky',
										is_regex: true
									},
									{
										col1: '_gid',
										col2: 'google.com',
										col3: '24 hodín'
									},
									{
										col1: '_gat',
										col2: 'google.com',
										col3: '1 minúta',
										is_regex: true
									}
								]
							},
							{
								title: 'Viac informácií',
								description: 'Ak máte akékoľvek otázky týkajúce sa našich zásad používania súborov cookie a vašich možností, prosím <a class="cc-link" href="https://www.schneider-electric.sk/sk/work/support" target="_blank" rel="noopener noreferrer">kontaktujte nás</a>.'
							}
						]
					}
				},
				'pl': {
					consent_modal: {
						title: 'Ustawienia plików cookie 🍪',
						description: 'Ta strona korzysta z plików cookie. <button type="button" data-cc="c-settings" class="cc-link">Więcej informacji</button>',
						primary_btn: {
							text: 'Akceptuj',
							role: 'accept_all'
						},
						secondary_btn: {
							text: 'Odrzuć',
							role: 'accept_necessary'
						}
					},
					settings_modal: {
						title: 'Ustawienia plików cookie',
						save_settings_btn: 'Zapisz ustawienia',
						accept_all_btn: 'Zaakceptuj wszystkie',
						reject_all_btn: 'Odrzuć wszystkie',
						close_btn_label: 'Zamknij',
						cookie_table_headers: [
							{col1: 'Nazwa'},
							{col2: 'Domena'},
							{col3: 'Wygaśnięcie'}
						],
						blocks: [
							{
								title: 'Korzystanie z plików cookie 📢',
								description: 'Używamy plików cookie, aby zapewnić podstawowe funkcje strony i poprawić Twoje wrażenia online. Dla każdej kategorii możesz w dowolnym momencie zdecydować, czy chcesz ją włączyć/wyłączyć. Więcej szczegółów dotyczących plików cookie i innych danych wrażliwych znajdziesz w pełnej treści <a href="https://www.se.com/pl/pl/about-us/legal/data-privacy" class="cc-link" target="_blank" rel="noopener noreferrer">polityki prywatności</a>.'
							}, {
								title: 'Pliki cookie niezbędne technicznie',
								description: 'Te pliki cookie są niezbędne do prawidłowego działania strony internetowej. Bez nich strona nie działałaby poprawnie.',
								toggle: {
									value: 'necessary',
									enabled: true,
									readonly: true
								}
							}, {
								title: 'Analityczne pliki cookie',
								description: 'Te pliki cookie pomagają nam zrozumieć, w jaki sposób odwiedzający korzystają ze strony. Są zbierane anonimowo i uruchamiane wyłącznie za Twoją zgodą.',
								toggle: {
									value: 'analytics',
									enabled: false,
									readonly: false
								},
								cookie_table: [
									{
										col1: '_ga',
										col2: 'google.com',
										col3: '2 lata',
										is_regex: true
									},
									{
										col1: '_gid',
										col2: 'google.com',
										col3: '24 godziny'
									},
									{
										col1: '_gat',
										col2: 'google.com',
										col3: '1 minuta',
										is_regex: true
									}
								]
							},
							{
								title: 'Więcej informacji',
								description: 'Jeśli masz jakiekolwiek pytania dotyczące naszej polityki plików cookie i Twoich wyborów, prosimy o <a class="cc-link" href="https://www.se.com/pl/pl/work/support" target="_blank" rel="noopener noreferrer">kontakt</a>.'
							}
						]
					}
				}
			},
		});
	}
}
